import React, { useMemo } from 'react';
import type { TableProps } from 'antd';
import { AppButton, AppTable, AppPagination, AppTableActionButton } from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import { renderTableCellTruncate } from '@/utils/tableCellTruncate';
import type { DocumentTemplate, PaginationInfo } from '@/types/documentTemplate.types';

interface DocumentTemplateListSectionProps {
  templates: DocumentTemplate[];
  pagination: PaginationInfo | null;
  onNew: () => void;
  onEdit: (template: DocumentTemplate) => void;
  onDelete: (template: DocumentTemplate) => void;
  onPageChange: (page: number) => void;
  disableCreate?: boolean;
  disableEdit?: boolean;
  disableDelete?: boolean;
}

const DocumentTemplateListSection: React.FC<DocumentTemplateListSectionProps> = ({
  templates,
  pagination,
  onNew,
  onEdit,
  onDelete,
  onPageChange,
  disableCreate = false,
  disableEdit = false,
  disableDelete = false,
}) => {
  const tableColumns: TableProps<DocumentTemplate>['columns'] = useMemo(
    () => [
      {
        title: 'No',
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: 50,
        align: 'center',
      },
      {
        title: '種類',
        dataIndex: 'document_type',
        key: 'document_type',
        render: (_, record) => record.document_type?.name,
      },
      {
        title: 'デフォルト',
        dataIndex: 'is_default',
        key: 'is_default',
        width: 100,
        align: 'center',
        render: (isDefault: boolean) => (isDefault ? '〇' : ''),
      },
      {
        title: '敬称',
        dataIndex: 'suffix_type',
        key: 'suffix_type',
        width: 100,
        render: (_, record) => record.suffix_type?.name,
      },
      {
        title: '表題',
        dataIndex: 'title',
        key: 'title',
        width: 360,
        render: text => renderTableCellTruncate(text, { width: 360 }),
      },
      {
        title: '本文',
        dataIndex: 'content',
        key: 'content',
        width: 360,
        render: text => renderTableCellTruncate(text, { width: 360 }),
      },
      {
        title: '操作',
        key: 'action',
        align: 'center',
        className: '!w-0 whitespace-nowrap',
        width: 50,
        render: (_, record) => (
          <div className="flex justify-center gap-1 px-2 w-fit">
            <AppTableActionButton
              action="edit"
              onClick={() => onEdit(record)}
              disabled={disableEdit}
              title="編集"
            >
              編集
            </AppTableActionButton>
            <AppTableActionButton
              action="delete"
              onClick={() => onDelete(record)}
              disabled={disableDelete}
              title="削除"
            >
              削除
            </AppTableActionButton>
          </div>
        ),
      },
    ],
    [disableDelete, disableEdit, onDelete, onEdit]
  );

  const total = pagination?.total || 0;

  return (
    <CollapsibleCard>
      {/* Header: Count display (left) and New button (right) - Stack on mobile, horizontal on tablet+ */}
      <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between">
        <div className="text-sm font-medium text-center text-gray-700 md:text-left">
          検索結果: {total}件
        </div>
        <AppButton
          type="primary"
          onClick={onNew}
          disabled={disableCreate}
          className="flex items-center justify-center gap-2 px-4 py-2 font-medium md:w-auto"
        >
          <i className="fa fa-plus" />
          新規登録
        </AppButton>
      </div>

      {/* Table - Auto layout with last column fit */}
      <div className="app-table-last-col-fit">
        <AppTable
          columns={tableColumns}
          dataSource={templates}
          rowKey="id"
          pagination={false}
          tableLayout="fixed"
          loading={false}
        />
      </div>

      {/* Pagination */}
      <div className="flex justify-center mt-4">
        <AppPagination
          current={pagination?.current_page}
          total={pagination?.total}
          pageSize={pagination?.per_page}
          onChange={onPageChange}
          hideOnSinglePage
        />
      </div>
    </CollapsibleCard>
  );
};

export default DocumentTemplateListSection;
